import React, { useEffect, useMemo, useState } from 'react';
import SpinnerLoader from '../../components/spinnerLoader';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card, Typography, Tag,
  Space, Button, Form, Input, Select, DatePicker,
  Switch, InputNumber,
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, SaveOutlined, CloseOutlined,
  MailOutlined, PhoneOutlined, FileTextOutlined,
  DeleteOutlined, PlusOutlined,
  IdcardOutlined, DollarOutlined, EyeOutlined, CalendarOutlined, HistoryOutlined, SolutionOutlined,
  ScheduleOutlined,
} from '@ant-design/icons';
import { enrollmentActions } from '../../actions/enrollmentActions';
import { categoryActions } from '../../actions/categoryActions';
import { commonActions } from '../../actions/commonActions';
import { feeActions } from '../../actions/feeActions';
import dayjs from 'dayjs';
import GuardedButton from '../../components/guardedButton';
import enrollmentCourse from '../../../public/assets/follower.png';
import { useCurrentPageAccess } from '../../services/helper';

const { Text } = Typography;

interface EnrollmentViewProps {
  role?: string;
}

const EnrollmentView: React.FC<EnrollmentViewProps> = ({ role }) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const pageAccess = useCurrentPageAccess();
  const enrollmentId = location.state?.enrollmentId;
  const enrollmentType = location.state?.serviceType || null;
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);
  const [enrollment, setEnrollment] = useState<any>(null);
  const [editableDocuments, setEditableDocuments] = useState<any[]>([]);

  const enrollmentView = useSelector((state: any) => state.getEnrollmentViewReducer);
  const enrollmentData = useSelector((state: any) => state.createEnrollmentReducer);
  const categoryData = useSelector((state: any) => state.getCategoryReducer);
  const serviceData = useSelector((state: any) => state.getServiceReducer);
  const feeHistoryData = useSelector((state: any) => state.getEnrollmentFeeHistoryReducer);
  const subscriptionStatus = useSelector((state: any) => state.userSubscriptionStatusReducer);
  const hasAccountingAccess = Boolean(subscriptionStatus?.data?.routes?.includes('/accounting'));

  const [paymentSummary, setPaymentSummary] = useState<{ totalFee: number; totalPaid: number; pendingAmount: number } | null>(null);

  useEffect(() => {
    const fees: any[] = feeHistoryData?.fees ?? [];
    if (fees.length > 0) {
      const totalPaid = fees.reduce((sum: number, f: any) => sum + (f.paidAmount || 0), 0);
      const totalFee = fees[0]?.totalFee || 0;
      setPaymentSummary({ totalFee, totalPaid, pendingAmount: Math.max(0, totalFee - totalPaid) });
    } else {
      setPaymentSummary(null);
    }
  }, [feeHistoryData]);

  const categoryOptions = useMemo(() => {
    const payload = categoryData?.categorydata;
    const list = payload?.categories ?? payload ?? [];
    return (Array.isArray(list) ? list : []).map((c: any) => ({
      label: c?.categoryName,
      value: c?._id,
    }));
  }, [categoryData]);

  const allServices = useMemo(() => {
    const payload = serviceData?.servicedata;
    const list = payload?.services ?? payload ?? [];
    return Array.isArray(list) ? list : [];
  }, [serviceData]);

  const serviceOptions = useMemo(() => {
    return allServices.map((s: any) => ({
      label: s?.serviceName,
      value: s?._id,
    }));
  }, [allServices]);

  const isTrainingEnrollment = useMemo(() => {
    if (enrollmentType === 'TRAINING') return true;
    const currentService = allServices.find((s: any) => s._id === enrollment?.serviceId);
    if (currentService?.serviceType === 'TRAINING' || currentService?.type === 'TRAINING') return true;
    return Boolean(enrollment?.noOfClasses && enrollment.noOfClasses > 0);
  }, [enrollmentType, allServices, enrollment]);

  useEffect(() => {
    if (enrollmentId) {
      dispatch(enrollmentActions.getEnrollmentView({ id: enrollmentId }));
      dispatch(categoryActions.getCategory({ isActive: true }));
      dispatch(commonActions.getService({ isActive: true }));
    }
  }, [enrollmentId, dispatch]);

  useEffect(() => {
    if (enrollmentView?.isSuccess && enrollmentView?.enrollmentview) {
      const viewData = enrollmentView.enrollmentview;
      const transformedEnrollment = {
        _id: viewData.enrolmentId,
        enrolledNumber: viewData.enrolledNumber,
        studentName: `${viewData.student?.firstName || ''} ${viewData.student?.lastName || ''}`.trim(),
        studentEmail: viewData.student?.email,
        studentPhone: viewData.student?.phone,
        categoryName: viewData.category?.categoryName,
        serviceName: viewData.service?.serviceName,
        servicePrice: viewData.service?.price,
        price: viewData.price,
        status: viewData.status,
        categoryId: viewData.category?._id,
        serviceId: viewData.service?._id,
        studentId: viewData.student?._id,
        drivingLicenseNo: viewData.drivingLicenseNo,
        dlIssuedDate: viewData.dlIssuedDate ? dayjs(viewData.dlIssuedDate) : null,
        rcNumber: viewData.rcNumber,
        documents: viewData.documents,
        attendanceSummary: viewData.attendanceSummary,
        applicationStatus: viewData.applicationStatus,
        applicationAssignedTo: viewData.applicationAssignedTo?._id,
        applicationAssignedToName: viewData.applicationAssignedTo
          ? `${viewData.applicationAssignedTo.firstName} ${viewData.applicationAssignedTo.lastName || ''}`.trim()
          : null,
        assignmentRemarks: viewData.assignmentRemarks,
        noOfClasses: viewData.noOfClasses,
        isSimulationRequired: viewData.isSimulationRequired,
        simulationClasses: viewData.simulationClasses,
        onRoadClasses: viewData.onRoadClasses,
        createdAt: viewData.createdAt,
      };
      setEnrollment(transformedEnrollment);

      if (transformedEnrollment._id && transformedEnrollment.studentId) {
        dispatch(feeActions.getEnrollmentFeeHistory({
          enrollmentId: transformedEnrollment._id,
          studentId: transformedEnrollment.studentId,
        }));
      }
    }
  }, [enrollmentView?.isSuccess, enrollmentView?.enrollmentview]);

  useEffect(() => {
    if (enrollmentData?.isSuccess) {
      setIsEditMode(false);
      dispatch(enrollmentActions.resetCreateEnrollment());
      const refetchId = enrollmentId || enrollment?._id;
      if (refetchId) dispatch(enrollmentActions.getEnrollmentView({ id: refetchId }));
    }
  }, [enrollmentData?.isSuccess]);

  const handleEdit = () => {
    if (enrollment) {
      form.setFieldsValue({
        price: enrollment.price,
        status: enrollment.status,
        categoryId: enrollment.categoryId,
        serviceId: enrollment.serviceId,
        applicationStatus: enrollment.applicationStatus,
        noOfClasses: enrollment.noOfClasses,
        isSimulationRequired: enrollment.isSimulationRequired,
        simulationClasses: enrollment.simulationClasses,
        onRoadClasses: enrollment.onRoadClasses,
        documents: enrollment.documents,
        drivingLicenseNo: enrollment.drivingLicenseNo,
        dlIssuedDate: enrollment.dlIssuedDate ? dayjs(enrollment.dlIssuedDate) : null,
      });
      setEditableDocuments(enrollment.documents || []);
      setIsEditMode(true);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditableDocuments([]);
    form.resetFields();
  };

  const handleSave = () => {
    form.validateFields().then(values => {
      if (values.isSimulationRequired && values.noOfClasses) {
        const total = (values.simulationClasses || 0) + (values.onRoadClasses || 0);
        if (total !== values.noOfClasses) {
          form.setFields([{ name: 'simulationClasses', errors: [`Simulation (${values.simulationClasses || 0}) + On-Road (${values.onRoadClasses || 0}) must equal No. of Classes (${values.noOfClasses})`] }]);
          return;
        }
      }
      const documentsArray = editableDocuments.map((doc: any) => ({
        label: doc.label, status: doc.status, required: doc.required ?? false,
      }));
      const payload = {
        _id: enrollment?._id,
        price: values.price,
        status: values.status,
        categoryId: values.categoryId || enrollment?.categoryId,
        studentId: enrollment?.studentId,
        serviceId: values.serviceId || enrollment?.serviceId,
        documents: documentsArray,
        applicationStatus: values.applicationStatus,
        applicationAssignedTo: enrollment?.applicationAssignedTo,
        noOfClasses: values.noOfClasses,
        isSimulationRequired: values.isSimulationRequired,
        simulationClasses: values.simulationClasses,
        onRoadClasses: values.onRoadClasses,
        drivingLicenseNo: values.drivingLicenseNo || null,
        dlIssuedDate: values.dlIssuedDate?.toISOString() || null,
      };
      dispatch(enrollmentActions.createEnrollment(payload));
    });
  };

  const handleAddDocument = () => {
    setEditableDocuments([...editableDocuments, { label: '', status: 'PENDING', required: false }]);
  };

  const handleRemoveDocument = (index: number) => {
    setEditableDocuments(editableDocuments.filter((_, i) => i !== index));
  };

  const handleDocumentChange = (index: number, field: string, value: any) => {
    const newDocs = [...editableDocuments];
    newDocs[index] = { ...newDocs[index], [field]: value };
    setEditableDocuments(newDocs);
  };

  const getAppStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      'ASSIGNED': 'blue', 'APPROVED': 'green', 'REJECTED': 'red',
      'SUBMITTED': 'orange', 'COMPLETED': 'cyan',
    };
    return colorMap[status] || 'default';
  };

  const getEnrollmentStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      'ASSIGNED': 'blue',
      'IN_PROGRESS': 'orange',
      'COMPLETED': 'green',
      'CANCELLED': 'red',
    };
    return colorMap[status] || 'default';
  };

  if (enrollmentView?.isLoading) {
    return <div className='view-loading'><SpinnerLoader /></div>;
  }

  if (!enrollment) {
    return (
      <div style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/enrollments')}>Back</Button>
        <Card style={{ marginTop: 16 }}><Text type="secondary">Enrollment not found</Text></Card>
      </div>
    );
  }

  const statusKey = (enrollment.status || '').toUpperCase();
  const statusClass = statusKey === 'COMPLETED' ? 'active' : statusKey === 'IN_PROGRESS' ? 'pending' : statusKey === 'CANCELLED' ? 'danger' : 'info';
  const createdLabel = enrollment.createdAt ? dayjs(enrollment.createdAt).format('DD MMM YYYY') : null;
  const canEdit = (role === 'ADMIN' || role === 'SUPER_ADMIN') && pageAccess.isEdit;

  const fmtDate = (d: any) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-');

  let durationLabel: string | null = null;
  if (enrollment.startDate && enrollment.endDate) {
    const days = dayjs(enrollment.endDate).diff(dayjs(enrollment.startDate), 'day');
    if (days > 0) durationLabel = days >= 30 ? `${Math.round(days / 30)} mo` : `${days} days`;
  }

  const sessDetails: any[] = enrollment.attendanceSummary?.attendanceDetails || [];
  const sessPresent = sessDetails.filter((d: any) => (d.status || '').toUpperCase() === 'PRESENT').length;
  const sessTotal = sessDetails.length;

  const navState = { enrollmentId, studentId: enrollment.studentId, studentName: enrollment.studentName, serviceType: enrollmentType };
  const goToSessions = () => navigate('/enrollments/sessions', { state: navState });
  const goToPayments = () => navigate('/enrollments/payments', { state: navState });

  const documents: any[] = enrollment.documents || [];

  // ── Reusable Documents Panel (view mode) ──
  const DocumentsPanel = ({ docs }: { docs: any[] }) => {
    const uploadedCount = docs.filter((d: any) => (d.status || '').toUpperCase() === 'UPLOADED').length;
    const totalCount = docs.length;
    const pct = totalCount > 0 ? Math.round((uploadedCount / totalCount) * 100) : 0;
    const radius = 22;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (pct / 100) * circumference;

    return (
      <div className="enr-docs-panel">
        {/* Panel Header */}
        <div className="enr-docs-panel-header">
          <div className="enr-docs-panel-title-row">
            <div className="enr-docs-panel-title">
              <FileTextOutlined className="enr-docs-panel-title-icon" />
              <span>Documents</span>
            </div>
            {docs.length > 0 && (
              <div className="enr-docs-panel-counters">
                <span className="enr-docs-counter enr-docs-counter--uploaded">
                  <svg width="8" height="8" viewBox="0 0 8 8"><circle cx="4" cy="4" r="4" fill="#16a34a" /></svg>
                  {uploadedCount} uploaded
                </span>
                <span className="enr-docs-counter enr-docs-counter--required">
                  <svg width="8" height="8" viewBox="0 0 8 8"><circle cx="4" cy="4" r="4" fill="#dc2626" /></svg>
                  {docs.filter((d: any) => d.required && (d.status || '').toUpperCase() !== 'UPLOADED').length} required
                </span>
              </div>
            )}
          </div>

          {/* Circular progress + bar */}
          {docs.length > 0 && (
            <div className="enr-docs-progress-row">
              <div className="enr-docs-circular-wrap">
                <svg width="60" height="60" viewBox="0 0 60 60" className="enr-docs-circular-svg">
                  <circle cx="30" cy="30" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="5" />
                  <circle
                    cx="30" cy="30" r={radius}
                    fill="none"
                    stroke={pct === 100 ? '#16a34a' : '#2563eb'}
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    transform="rotate(-90 30 30)"
                    style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                  />
                </svg>
                <span className="enr-docs-circular-pct" style={{ color: pct === 100 ? '#16a34a' : '#2563eb' }}>{pct}%</span>
              </div>
              <div className="enr-docs-progress-meta">
                <span className="enr-docs-progress-label">{uploadedCount} of {totalCount} documents uploaded</span>
                <div className="enr-docs-progress-bar-track">
                  <div
                    className="enr-docs-progress-bar-fill"
                    style={{ width: `${pct}%`, background: pct === 100 ? '#16a34a' : '#2563eb' }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Document rows */}
        {docs.length > 0 ? (
          <div className="enr-docs-list">
            {docs.map((doc: any, index: number) => {
              const isUploaded = (doc.status || '').toUpperCase() === 'UPLOADED';
              return (
                <div className={`enr-doc-item ${isUploaded ? 'enr-doc-item--done' : 'enr-doc-item--pending'}`} key={doc._id || index}>
                  <span className="enr-doc-item-icon">
                    {isUploaded ? (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="8" fill="#dcfce7" />
                        <path d="M4.5 8l2.5 2.5 4.5-4.5" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="7.25" stroke="#d1d5db" strokeWidth="1.5" fill="#f9fafb" />
                        <path d="M8 5v3m0 2h.01" stroke="#9ca3af" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    )}
                  </span>
                  <span className="enr-doc-item-name">{doc.label || 'Document'}</span>
                  <div className="enr-doc-item-badges">
                    {doc.required && <span className="enr-doc-badge enr-doc-badge--req">Required</span>}
                    <span className={`enr-doc-badge ${isUploaded ? 'enr-doc-badge--uploaded' : 'enr-doc-badge--pending'}`}>
                      {isUploaded ? 'Uploaded' : 'Pending'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="enr-docs-empty">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="#f1f5f9" />
              <path d="M10 9h8l4 4v10a1 1 0 01-1 1H10a1 1 0 01-1-1V10a1 1 0 011-1z" stroke="#94a3b8" strokeWidth="1.5" fill="none" />
              <path d="M18 9v4h4" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <span>No documents added</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="batch-view-page user-view-page">
      {/* Header */}
      <div className="batch-view-header">
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/enrollments  ')} className="batch-back-btn">Enrollments</Button>
        <div className="batch-header-right">
          {createdLabel && <span className="batch-created">Created {createdLabel}</span>}
          {!isEditMode ? (
            canEdit && (
              <GuardedButton className="batch-edit-btn" icon={<EditOutlined />} onClick={handleEdit}>Edit Enrollment</GuardedButton>
            )
          ) : (
            <Space>
              <Button icon={<CloseOutlined />} onClick={handleCancel}>Cancel</Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={enrollmentData?.isLoading}>Save</Button>
            </Space>
          )}
        </div>
      </div>

      {/* Title / hero */}
      <div className="batch-hero">
        <div className="batch-hero-icon">{enrollment.studentName?.charAt(0)?.toUpperCase() || 'S'}</div>
        <div className="batch-hero-info">
          <div className="batch-hero-titlerow">
            <h2 className="batch-hero-name">{enrollment.studentName}</h2>
            <span className={`batch-status-pill batch-status-pill--${statusClass}`}>
              <span className="batch-status-dot" />
              {enrollment.status}
            </span>
          </div>
          <div className="batch-hero-meta">
            {enrollment.enrolledNumber && <span className="batch-hero-meta-item"><IdcardOutlined /> {enrollment.enrolledNumber}</span>}
            {enrollment.studentEmail && <span className="batch-hero-meta-item"><MailOutlined /> {enrollment.studentEmail}</span>}
            {enrollment.studentPhone && <span className="batch-hero-meta-item"><PhoneOutlined /> {enrollment.studentPhone}</span>}
            {enrollment.rcNumber && <span className="batch-hero-meta-item"><FileTextOutlined /> RC: {enrollment.rcNumber}</span>}
          </div>
        </div>
      </div>

      {/* ── EDIT MODE ── */}
      {isEditMode ? (
        <Form form={form} layout="vertical" className="batch-view-form">
          {/* Enrollment Information */}
          <section className="uv-section uv-section--divider">
            <div className="uv-card-header">
              <ScheduleOutlined />
              <span className="uv-card-header-title">Enrollment & Schedule</span>
            </div>
            <div className="uv-personal-grid">
              <div className="uv-pd-item">
                <span className="uv-pd-label">Category</span>
                <Form.Item name="categoryId" style={{ marginBottom: 0 }}>
                  <Select options={categoryOptions} placeholder="Select category" />
                </Form.Item>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Service</span>
                <Form.Item name="serviceId" style={{ marginBottom: 0 }}>
                  <Select options={serviceOptions} placeholder="Select service" />
                </Form.Item>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Price</span>
                <Form.Item name="price" rules={[{ required: true, message: 'Required' }]} style={{ marginBottom: 0 }}>
                  <InputNumber prefix="₹" style={{ width: '100%' }} min={0} />
                </Form.Item>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Status</span>
                <Form.Item name="status" style={{ marginBottom: 0 }}>
                  <Select options={[
                    { label: 'Assigned', value: 'ASSIGNED' },
                    { label: 'In Progress', value: 'IN_PROGRESS' },
                    { label: 'Completed', value: 'COMPLETED' },
                    { label: 'Cancelled', value: 'CANCELLED' },
                  ]} />
                </Form.Item>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Application Status</span>
                <Form.Item name="applicationStatus" style={{ marginBottom: 0 }}>
                  <Select allowClear placeholder="Select status" options={[
                    { label: 'Assigned', value: 'ASSIGNED' },
                    { label: 'Submitted', value: 'SUBMITTED' },
                    { label: 'Approved', value: 'APPROVED' },
                    { label: 'Rejected', value: 'REJECTED' },
                    { label: 'Completed', value: 'COMPLETED' },
                  ]} />
                </Form.Item>
              </div>
            </div>

            {/* Training / Class Details (Only rendered for Training Services) */}
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.serviceId !== cur.serviceId || prev.isSimulationRequired !== cur.isSimulationRequired}>
              {() => {
                const currentServiceId = form.getFieldValue('serviceId') || enrollment?.serviceId;
                const matchedService = allServices.find((s: any) => s._id === currentServiceId);
                const isTraining = (matchedService?.serviceType === 'TRAINING' || matchedService?.type === 'TRAINING' || enrollmentType === 'TRAINING');
                if (!isTraining) return null;

                const isSimReq = form.getFieldValue('isSimulationRequired');
                return (
                  <div className="uv-personal-grid" style={{ marginTop: 16 }}>
                    <div className="uv-pd-item">
                      <span className="uv-pd-label">No. of Classes</span>
                      <Form.Item name="noOfClasses" style={{ marginBottom: 0 }}>
                        <InputNumber
                          style={{ width: '100%' }}
                          min={1}
                          precision={0}
                          step={1}
                          placeholder="e.g. 20"
                        />
                      </Form.Item>
                    </div>
                    <div className="uv-pd-item">
                      <span className="uv-pd-label">Simulation Required</span>
                      <Form.Item name="isSimulationRequired" valuePropName="checked" style={{ marginBottom: 0 }}>
                        <Switch />
                      </Form.Item>
                    </div>
                    {isSimReq && (
                      <>
                        <div className="uv-pd-item">
                          <span className="uv-pd-label">Simulation Classes</span>
                          <Form.Item name="simulationClasses" style={{ marginBottom: 0 }}>
                            <InputNumber style={{ width: '100%' }} min={0} precision={0} step={1} placeholder="Simulation classes" />
                          </Form.Item>
                        </div>
                        <div className="uv-pd-item">
                          <span className="uv-pd-label">On-Road Classes</span>
                          <Form.Item name="onRoadClasses" style={{ marginBottom: 0 }}>
                            <InputNumber style={{ width: '100%' }} min={0} precision={0} step={1} placeholder="On-road classes" />
                          </Form.Item>
                        </div>
                      </>
                    )}
                  </div>
                );
              }}
            </Form.Item>
          </section>

          <section style={{ display: 'flex', flexDirection: "column" }}>
            <section className="uv-section uv-section--divider" style={{ width: "50%" }}>
              <div className="uv-card-header">
                <FileTextOutlined className="uv-card-header-anticon" />
                <span className="uv-card-header-title">Documents</span>
              </div>
              <div className="enr-docs-edit-list">
                {editableDocuments.map((doc: any, index: number) => (
                  <div className="enr-docs-edit-row" key={index}>
                    <Input placeholder="Document name"
                      value={doc.label} onChange={e => handleDocumentChange(index, 'label', e.target.value)}
                      style={{ flex: 2 }}
                    />
                    <Select
                      value={doc.status}
                      onChange={v => handleDocumentChange(index, 'status', v)}
                      style={{ flex: 1 }}
                      options={[
                        { label: 'Pending', value: 'PENDING' },
                        { label: 'Uploaded', value: 'UPLOADED' },
                      ]}
                    />
                    <div className="enr-docs-edit-required">
                      <Switch
                        size="small"
                        checked={doc.required}
                        onChange={v => handleDocumentChange(index, 'required', v)}
                      />
                      <span className="enr-docs-edit-required-label">Required</span>
                    </div>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveDocument(index)}
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleAddDocument}
                  className="enr-docs-add-btn"
                >
                  Add Document
                </Button>
              </div>
            </section>
          </section>

          {/* License — shown when status is COMPLETED */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.status !== cur.status}>
            {() => {
              const currentStatus = form.getFieldValue('status');
              if (currentStatus !== 'COMPLETED') return null;
              return (
                <section className="uv-section uv-section--divider">
                  <div className="uv-card-header">
                    <IdcardOutlined className="uv-card-header-anticon" />
                    <span className="uv-card-header-title">License Details</span>
                  </div>
                  <div className="uv-personal-grid">
                    <div className="uv-pd-item">
                      <span className="uv-pd-label">DL Number</span>
                      <Form.Item name="drivingLicenseNo" rules={[{ required: true, message: 'DL number is required for completed enrollment' }]} style={{ marginBottom: 0 }}>
                        <Input placeholder="DL no. (TN01202XXXXXX)" />
                      </Form.Item>
                    </div>
                    <div className="uv-pd-item">
                      <span className="uv-pd-label">DL Issued Date</span>
                      <Form.Item name="dlIssuedDate" rules={[{ required: true, message: 'DL issued date is required' }]} style={{ marginBottom: 0 }}>
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </div>
                  </div>
                </section>
              );
            }}
          </Form.Item>
        </Form>
      ) : (
        /* ── VIEW MODE ── */
        <>
          {/* Enrollment Information */}
          <section className="uv-section uv-section--divider">
            <div className="uv-card-header">
              <ScheduleOutlined />
              <span className="uv-card-header-title">Enrollment & Schedule</span>
            </div>
            <div className="uv-personal-grid">
              <div className="uv-pd-item">
                <span className="uv-pd-label">Category</span>
                <span className="uv-pd-value">{enrollment.categoryName || '—'}</span>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Service</span>
                <span className="uv-pd-value">{enrollment.serviceName || '—'}</span>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Price</span>
                <span className="uv-pd-value">{enrollment.price != null ? `₹${enrollment.price.toLocaleString()}` : '—'}</span>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Status</span>
                <span className="uv-pd-value">
                  {enrollment.status ? (
                    <Tag style={{ border: "1px solid", borderRadius: "8px" }} color={getEnrollmentStatusColor(enrollment.status)}>
                      {enrollment.status}
                    </Tag>
                  ) : '—'}
                </span>
              </div>
              <div className="uv-pd-item">
                <span className="uv-pd-label">Application Status</span>
                <span className="uv-pd-value">
                  {enrollment.applicationStatus ? (
                    <Tag style={{ border: "1px solid", borderRadius: "8px" }} color={getAppStatusColor(enrollment.applicationStatus)}>
                      {enrollment.applicationStatus}
                    </Tag>
                  ) : '—'}
                </span>
              </div>

              {enrollment.applicationAssignedToName && (
                <div className="uv-pd-item">
                  <span className="uv-pd-label">Assigned To</span>
                  <span className="uv-pd-value">{enrollment.applicationAssignedToName}</span>
                </div>
              )}
              {enrollment.drivingLicenseNo && (
                <div className="uv-pd-item">
                  <span className="uv-pd-label">DL Number</span>
                  <span className="uv-pd-value">{enrollment.drivingLicenseNo}</span>
                </div>
              )}
              {enrollment.dlIssuedDate && (
                <div className="uv-pd-item">
                  <span className="uv-pd-label">DL Issued Date</span>
                  <span className="uv-pd-value">{fmtDate(enrollment.dlIssuedDate)}</span>
                </div>
              )}

              {/* Training fields in View Mode */}
              {isTrainingEnrollment && enrollment.noOfClasses != null && enrollment.noOfClasses > 0 && (
                <div className="uv-pd-item"><span className="uv-pd-label">Number of Classes</span><span className="uv-pd-value">{enrollment.noOfClasses}</span></div>
              )}
              {isTrainingEnrollment && enrollment.isSimulationRequired != null && (
                <div className="uv-pd-item"><span className="uv-pd-label">Simulation Required</span><span className="uv-pd-value">{enrollment.isSimulationRequired ? 'Yes' : 'No'}</span></div>
              )}
              {isTrainingEnrollment && enrollment.isSimulationRequired && (
                <>
                  <div className="uv-pd-item"><span className="uv-pd-label">Simulation Classes</span><span className="uv-pd-value">{enrollment.attendanceSummary?.attendedSimulation || 0} / {enrollment.simulationClasses || 0}</span></div>
                  <div className="uv-pd-item"><span className="uv-pd-label">On-Road Classes</span><span className="uv-pd-value">{enrollment.attendanceSummary?.attendedOnRoad || 0} / {enrollment.onRoadClasses || 0}</span></div>
                </>
              )}
            </div>
          </section>

          <section style={{ marginBottom: 20 }}>
            <div className="enr-docs-remarks-row">
              <div className="enr-docs-remarks-col">
                <DocumentsPanel docs={documents} />
              </div>

              <div className="enr-docs-remarks-col">
                {enrollment.assignmentRemarks ? (
                  <div className="enr-remarks-panel">
                    <div className="enr-remarks-panel-header">
                      <FileTextOutlined className="enr-remarks-panel-icon" />
                      <span className="enr-remarks-panel-title">Assignment Remarks</span>
                    </div>
                    <div className="enr-remarks-panel-body">
                      <p className="enr-remarks-panel-text">{enrollment.assignmentRemarks}</p>
                    </div>
                  </div>
                ) : (
                  <div className="enr-remarks-panel enr-remarks-panel--empty">
                    <div className="enr-remarks-panel-header">
                      <FileTextOutlined className="enr-remarks-panel-icon" />
                      <span className="enr-remarks-panel-title">Assignment Remarks</span>
                    </div>
                    <div className="enr-remarks-empty-state">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <rect width="28" height="28" rx="7" fill="#f1f5f9" />
                        <path d="M8 8h12M8 12h8M8 16h5" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      <span>No remarks added</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {(enrollmentType === "TRAINING" || hasAccountingAccess) && (
            <section className="uv-section">
              <div className="enr-quicklinks">
                {enrollmentType === "TRAINING" && (
                  <div className="enr-ql-row" onClick={goToSessions} role="button" tabIndex={0}>
                    <div className="enr-ql-left">
                      <span className="enr-ql-icon enr-ql-icon--sessions"><CalendarOutlined /></span>
                      <div className="enr-ql-text">
                        <span className="enr-ql-title">Sessions</span>
                        <span className="enr-ql-sub">
                          {sessTotal > 0
                            ? `${sessPresent} present · ${sessTotal}${enrollment.noOfClasses ? `/${enrollment.noOfClasses}` : ''} total`
                            : 'View attendance & driving analytics'}
                        </span>
                      </div>
                    </div>
                    <div className="enr-ql-right">
                      {sessTotal > 0 && (
                        <span className="enr-ql-badge enr-ql-badge--sessions">
                          {sessTotal}{enrollment.noOfClasses ? `/${enrollment.noOfClasses}` : ''} sessions
                        </span>
                      )}
                      <EyeOutlined className="enr-ql-arrow" />
                    </div>
                  </div>
                )}
                {enrollmentType === "TRAINING" && hasAccountingAccess && <div className="enr-ql-divider" />}
                {hasAccountingAccess && (
                  <div className="enr-ql-row" onClick={goToPayments} role="button" tabIndex={0}>
                    <div className="enr-ql-left">
                      <span className="enr-ql-icon enr-ql-icon--payments"><DollarOutlined /></span>
                      <div className="enr-ql-text">
                        <span className="enr-ql-title">Payments</span>
                        <span className="enr-ql-sub">
                          {paymentSummary
                            ? <>₹{paymentSummary.totalPaid.toLocaleString()} paid{paymentSummary.pendingAmount > 0 ? <> · <span className="enr-ql-pending">₹{paymentSummary.pendingAmount.toLocaleString()} pending</span></> : ' · fully cleared'}</>
                            : 'View transaction log & activity'}
                        </span>
                      </div>
                    </div>
                    <div className="enr-ql-right">
                      {paymentSummary && (
                        <span className="enr-ql-amount">
                          ₹{paymentSummary.totalFee.toLocaleString()} <span className="enr-ql-amount-label">total</span>
                        </span>
                      )}
                      <HistoryOutlined className="enr-ql-arrow" />
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default EnrollmentView;
